// deps
import { useCallback, useContext, useEffect, useRef, useState, useMemo } from "react";

// internal deps
import {
    AnalyticsContext,
    CheckoutActionTypes,
    CheckoutContext,
    NEWRELIC_TABLES,
    recordNREvent,
    useSessionStorage,
    GlobalHeaderState,
} from "@swiggy-private/checkout-core";
import { SESSION_STORAGE_KEYS, useEffectOnce, Storage } from "@swiggy-private/core-ui";

// analytics
import { onSplitPayToggleEvent } from "@payment-client/analytics/events/Payment";

// contexts
import { PaymentPayContext } from "@payment-client/contexts/PaymentPayContext";
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";
import { CroutonContext } from "@payment-client/contexts/CroutonContext";
import { SplitPayListContext } from "@payment-client/contexts/SplitPayListContext";

// helpers
import { isEmpty } from "@swiggy-private/js-utils";
import { getCroutonState } from "@payment-client/helpers/SplitPay";
import { getSplitpayIdentifier, isSplitPayMethodSwiggyPay } from "@swiggy-private/payment-ui";

//constants
import { TEXTS, TIME_INTERVALS, ICONS } from "./constants";
import { SHOW_ONE_SPLITPAY_AT_A_TIME_TOAST } from "@payment-client/constants/StorageKeys";
import { OOPS_SOMETHING_WENT_WRONG } from "@payment-client/constants/TextDict";

// types
import { SplitPayActions, SplitPayMethodValues } from "@payment-client/interfaces/SplitPay";
import { SplitPayData } from "@swiggy-private/payment-ui";
import {
    crossClickEvent,
    rewardsHalfCardImpressionEvent,
    rewardsInHalfcardClickEvent,
    rewardSuccessfullyAppliedToastImpressionEvent,
    selectOneSplitpayToastImpressionEvent,
    splitpayClickEvent,
    splitpayGroupImpressionEvent,
    viewAllRewardsCtaClickEvent,
    viewAllRewardsCtaImpressionEvent,
} from "@payment-client/analytics/events/Splitpay";
import { useRequestForBillImage } from "@payment-client/hooks/UseRequestForBillImage";
import { useBillImages } from "@payment-client/hooks/UseBillImages";
import { SPLITPAY_SOURCE } from "@payment-client/constants/Splitpay";
import { MARKETPLACE_BUSINESS_LINE, PAYMENT_CODES, TOAST_TYPE } from "@swiggy-private/payment-types";
import { useBillImageWebview } from "@payment-client/hooks/UseBillImageWebview";
import { isSplitPayMethodMMT } from "@payment-client/helpers/SplitPay";

// headers
import { getMarketplaceBusinessLine } from "@payment-client/utils/Headers";
import { useExpenseCodeHalfCardContext } from "@payment-client/components/show-payments/components/expense-code-provider-halfcard";
import useBillToCompanySplitPay from "./useBillToCompanySplitPay";

const useSplitPay = (): SplitPayActions => {
    const { billTotal, setSplitPayMethods, selectedSplitPayMethods } = useContext(PaymentPayContext);
    const { setCroutonState } = useContext(CroutonContext);
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const { refetchPayments } = useContext(ShowPaymentContext);
    const { splitPayList } = useContext(SplitPayListContext);
    const croutonTimer = useRef<NodeJS.Timeout | undefined>();
    const { requestForBillImage } = useRequestForBillImage();
    const { clearBillImages } = useBillImages();
    const { setCheckoutState } = useContext(CheckoutContext);
    const { openBillImageWebview } = useBillImageWebview();
    const { handleSplitPayMethodSelection } = useBillToCompanySplitPay();

    const {
        showExpenseCodeHalfCard,
        isBillToCompanySelected,
        onOpenHalfcard,
        setOnSubmit,
        setSplitPaySelectedMethods,
    } = useExpenseCodeHalfCardContext();
    const headers = GlobalHeaderState.getHeaders();

    const [isRewardsBottomSheetVisible, setIsRewardsBottomSheetVisible] = useState(false);

    const isMarketplaceBusinessLineRegular = useMemo(() => {
        return getMarketplaceBusinessLine(headers) === MARKETPLACE_BUSINESS_LINE.REGULAR;
    }, [headers]);

    const [showOneSplitPayToastCount, setShowOneSplitpayToastCount] = useSessionStorage<number>(
        SHOW_ONE_SPLITPAY_AT_A_TIME_TOAST,
        0,
    );

    const _setIsRewardsBottomSheetVisible = useCallback((val: boolean) => {
        setIsRewardsBottomSheetVisible(val);
    }, []);

    const _splitpayGroupImpressionEvent = useCallback(() => {
        splitpayGroupImpressionEvent(impressionEvent);
    }, [impressionEvent]);

    const _splitpayClickEvent = useCallback(
        (context: string) => {
            splitpayClickEvent(context, clickEvent);
        },
        [clickEvent],
    );

    const _viewAllRewardsCtaImpressionEvent = useCallback(() => {
        viewAllRewardsCtaImpressionEvent(impressionEvent);
    }, [impressionEvent]);

    const _viewAllRewardsCtaClickEvent = useCallback(() => {
        viewAllRewardsCtaClickEvent(clickEvent);
    }, [clickEvent]);

    const _rewardsHalfCardImpressionEvent = useCallback(() => {
        rewardsHalfCardImpressionEvent(impressionEvent);
    }, [impressionEvent]);

    const _rewardsInHalfcardClickEvent = useCallback(
        (rewardName: string, selected: boolean) => {
            rewardsInHalfcardClickEvent(rewardName, selected, clickEvent);
        },
        [clickEvent],
    );

    const _rewardSuccessfullyAppliedToastImpressionEvent = useCallback(
        (text: string) => {
            rewardSuccessfullyAppliedToastImpressionEvent(text, impressionEvent);
        },
        [impressionEvent],
    );

    const isMarketplaceBusinessLineDineout = useMemo(() => {
        return getMarketplaceBusinessLine(headers) === MARKETPLACE_BUSINESS_LINE.DINEOUT;
    }, [headers]);

    const _selectOneSplitpayToastImpressionEvent = useCallback(
        (text: string) => {
            selectOneSplitpayToastImpressionEvent(text, impressionEvent);
        },
        [impressionEvent],
    );

    const _crossClickEvent = useCallback(() => {
        crossClickEvent(clickEvent);
    }, [clickEvent]);

    const showCroutons = useCallback(
        (methodData: SplitPayData, isSplitpaySelected: boolean) => {
            const croutonState = getCroutonState({
                methodData,
                selectedSplitPayMethods,
                splitpayList: splitPayList ?? [],
                showOneSplitPayToastCount,
                isSplitpaySelected,
            });
            if (!isEmpty(croutonState)) {
                setCroutonState(croutonState.state);
                if (croutonState.increaseOneSplitpayToastCount) {
                    setShowOneSplitpayToastCount(showOneSplitPayToastCount + 1);
                    _selectOneSplitpayToastImpressionEvent(croutonState.state.title);
                } else {
                    _rewardSuccessfullyAppliedToastImpressionEvent(croutonState.state.title);
                }
            }
        },
        [
            splitPayList,
            selectedSplitPayMethods,
            setCroutonState,
            setShowOneSplitpayToastCount,
            showOneSplitPayToastCount,
            _selectOneSplitpayToastImpressionEvent,
            _rewardSuccessfullyAppliedToastImpressionEvent,
        ],
    );

    const handleChangeCallback = useCallback(
        (isSplitpaySelected: boolean, methodData: SplitPayData, removeReward?: boolean) => {
            const splitPayMethod = isSplitpaySelected ? getSplitpayIdentifier(methodData) : "";

            setCroutonState({ title: "" });

            refetchPayments(splitPayMethod, () => {
                setSplitPayMethods(SplitPayMethodValues.DEFAULT);
                setCroutonState({
                    title: TEXTS.TOGGLE_FAILED_TEXT,
                    durationOfCrouton: TIME_INTERVALS.SPLITPAY_CROUTON_DISPLAY_TIME,
                    icon: ICONS.TOGGLE_ERROR,
                });
            });

            showCroutons(methodData, isSplitpaySelected);

            if (isSplitPayMethodSwiggyPay(methodData)) {
                onSplitPayToggleEvent({
                    selected: isSplitpaySelected,
                    orderAmount: billTotal,
                    clickEvent,
                    paymentCode: getSplitpayIdentifier(methodData),
                });
            }

            setSplitPayMethods(splitPayMethod);
            removeReward && methodData.isReward && Storage.removeItem(SESSION_STORAGE_KEYS.WITH_REWARD);
        },
        [billTotal, clickEvent, refetchPayments, setCroutonState, setSplitPayMethods, showCroutons],
    );

    const shouldTriggerExpenseCodeHalfCard = useCallback(
        (isSplitpaySelected: boolean, methodData: SplitPayData) => {
            return (
                isSplitpaySelected &&
                isSplitPayMethodMMT(methodData) &&
                showExpenseCodeHalfCard &&
                !isBillToCompanySelected
            );
        },
        [showExpenseCodeHalfCard, isBillToCompanySelected],
    );

    const onSplitPayToggle = useCallback(
        (isSplitpaySelected: boolean, methodData: SplitPayData, removeReward?: boolean) => {
            const handleChangeCallbackWithArgs = () =>
                handleChangeCallback(isSplitpaySelected, methodData, removeReward);

            if (isMarketplaceBusinessLineRegular && shouldTriggerExpenseCodeHalfCard(isSplitpaySelected, methodData)) {
                setOnSubmit(handleChangeCallbackWithArgs);
                onOpenHalfcard();

                // set splitpay method state if expense code halfcard is enabled to sync split pay state cart <> payments page
                setSplitPaySelectedMethods({
                    isSplitpaySelected,
                    methodData,
                });
                return;
            }

            if (
                isMarketplaceBusinessLineDineout &&
                methodData.paymentCode.toUpperCase() === PAYMENT_CODES.MMT.toUpperCase()
            ) {
                const isBillUploadRequired = methodData.meta?.billUploadRequired;
                const isWithBusinessDetails = methodData.meta?.showExpenseCodeHalfCard;

                if (!isSplitpaySelected && !isBillUploadRequired) {
                    // Clear bill images and then call the handleChangeCallback
                    clearBillImages()
                        .then(() => {
                            handleChangeCallbackWithArgs();
                        })
                        .catch((err) => {
                            recordNREvent({
                                eventType: NEWRELIC_TABLES.CLEAR_BILL_IMAGES_ERROR,
                                error: err instanceof Error ? err.name : JSON.stringify(err),
                            });

                            setCheckoutState({
                                type: CheckoutActionTypes.GENERIC_PAYMENT_TOAST,
                                payload: {
                                    message: OOPS_SOMETHING_WENT_WRONG,
                                    type: TOAST_TYPE.ERROR,
                                },
                            });
                        });

                    return;
                }

                if (isWithBusinessDetails) {
                    setOnSubmit(
                        isBillUploadRequired
                            ? () => openBillImageWebview(handleChangeCallbackWithArgs)
                            : handleChangeCallbackWithArgs,
                    );
                    onOpenHalfcard();
                    return;
                }

                if (isBillUploadRequired) {
                    requestForBillImage({
                        balance: methodData.eligibleBalance,
                        onBillImagesUpdated: handleChangeCallbackWithArgs,
                        source: SPLITPAY_SOURCE,
                    });
                    return;
                }

                /** when billUploadRequired and showExpenseCodeHalfCard both are false */
                handleChangeCallbackWithArgs();
                return;
            }

            handleChangeCallbackWithArgs();

            // share splitpay state cart <> payments page
            handleSplitPayMethodSelection(isSplitpaySelected, methodData);
        },
        [
            isMarketplaceBusinessLineRegular,
            shouldTriggerExpenseCodeHalfCard,
            isMarketplaceBusinessLineDineout,
            handleChangeCallback,
            setOnSubmit,
            onOpenHalfcard,
            clearBillImages,
            setCheckoutState,
            openBillImageWebview,
            requestForBillImage,
        ],
    );

    useEffect(() => {
        croutonTimer.current = setTimeout(() => {
            setCroutonState({ title: "" });
        }, TIME_INTERVALS.SPLITPAY_CROUTON_DISPLAY_TIME);
    }, [setCroutonState]);

    useEffectOnce(() => {
        return () => {
            !isEmpty(croutonTimer.current) && clearTimeout(croutonTimer.current);
        };
    });

    return {
        onSplitPayToggle,
        isRewardsBottomSheetVisible,
        setIsRewardsBottomSheetVisible: _setIsRewardsBottomSheetVisible,
        analyticsEvents: {
            splitpayGroupImpressionEvent: _splitpayGroupImpressionEvent,
            splitpayClickEvent: _splitpayClickEvent,
            viewAllRewardsCtaImpressionEvent: _viewAllRewardsCtaImpressionEvent,
            viewAllRewardsCtaClickEvent: _viewAllRewardsCtaClickEvent,
            rewardsHalfCardImpressionEvent: _rewardsHalfCardImpressionEvent,
            rewardsInHalfcardClickEvent: _rewardsInHalfcardClickEvent,
            crossClickEvent: _crossClickEvent,
        },
    };
};

export default useSplitPay;
